/**
 * 主服务 HTTP 请求封装
 * 
 * 【重要】此文件用于主服务接口请求，直接访问后端
 * 
 * 环境配置（env/.env.*）：
 * - VITE_SERVER_BASEURL: 主服务接口基础地址
 * - VITE_APP_PROXY_ENABLE: 是否启用代理
 * - VITE_APP_PROXY_PREFIX: 代理前缀路径
 * 
 * 特点：
 * 1. 经过项目统一的请求/响应拦截器（@/interceptors/request）
 * 2. 自动处理 token 过期和重新登录逻辑
 * 3. 统一的错误处理和提示
 * 4. 支持自动重试机制
 * 
 * 与 httpHk.ts 的区别：
 * - http.ts: 访问主服务（VITE_SERVER_BASEURL），经过统一拦截器
 * - httpHk.ts: 访问香港服务（VITE_HK_BASE_URL），独立请求处理
 */

import { prepareRequestAuth } from '@/interceptors/request'
import type { CustomRequestOptions } from '@/interceptors/request'
import { relogin } from '@/utils/login'
// #ifdef H5
import { LDBridge } from '@/utils/handleBrdge'
// #endif
let isRelogining = false
let reloginPromise: Promise<any> | null = null

export const http = async <T>(options: CustomRequestOptions, retry = false) => {
  await prepareRequestAuth('token')

  // 1. 返回 Promise 对象
  return new Promise<IResData<T>>((resolve, reject) => {
    uni.request({
      ...options,
      dataType: 'json',
      // #ifndef MP-WEIXIN
      responseType: 'json',
      timeout: 100000,
      // #endif
      // 响应成功
      async success(res) {
        // console.log(res, '????')
        // 状态码 2xx，参考 axios 的设计
        if (res.statusCode >= 200 && res.statusCode < 300) {
          // 2.1 提取核心数据 res.data
          if (
            typeof res.data === 'object' &&
            res.data !== null &&
            'code' in res.data &&
            (res.data as any).code === 0
          ) {
            resolve(res.data as IResData<T>)
          } else {
            if (
              typeof res.data === 'object' &&
              res.data !== null &&
              'code' in res.data &&
              ((res.data as any).code === 11012 || (res.data as any).code === 11011) &&
              !retry
            ) {
              console.log('11012')
              // #ifdef H5
              LDBridge.gotoLogin()
              // #endif
              // if (!uni.getStorageSync('phoneNumber') && !uni.getStorageSync('token')) {
              //   uni.switchTab({
              //     url: '/pages/index/index',
              //   })
              //   return
              // }
              // token过期，relogin后重试一次
              if (!isRelogining) {
                isRelogining = true
                reloginPromise = relogin({}).finally(() => {
                  isRelogining = false
                })
              }
              reloginPromise!
                .then((reloginRes) => {
                  if (reloginRes === 'success') {
                    // 重新请求，retry标记为true防止死循环
                    http<T>(options, true).then(resolve).catch(reject)
                  } else {
                    uni.showToast({
                      icon: 'none',
                      title: '自动登录失败，请重新登录',
                    })
                    reject(res.data)
                  }
                })
                .catch(() => {
                  uni.showToast({
                    icon: 'none',
                    title: '自动登录失败，请重新登录',
                  })
                  reject(res.data)
                })
              return
            }

            if((res.data as any).code === 62201) {
              reject(res.data)
              return 
            }

            uni.showToast({
              icon: 'none',
              title:
                typeof res.data === 'object' && res.data !== null && 'msg' in res.data
                  ? (res.data as any).msg || '请求错误'
                  : '请求错误',
            })
            reject(res.data)
          }
        } else if (res.statusCode === 401) {
          // 401错误  -> 清理用户信息，跳转到登录页
          // userStore.clearUserInfo()
          // uni.navigateTo({ url: '/pages/login/login' })
          reject(res)
        }  else {
          // 其他错误 -> 根据后端错误信息轻提示
          !options.hideErrorToast &&
           await uni.showToast({
              icon: 'none',
              title:
                typeof res.data === 'object' && res.data !== null && 'msg' in res.data
                  ? (res.data as any).msg || '请求错误'
                  : '请求错误',
                  duration: 3000
            })
          reject(res)
        }
      },
      // 响应失败
      fail(err) {
        uni.showToast({
          icon: 'none',
          title: '网络错误，换个网络试试',
        })
        reject(err)
      },
    })
  })
}

/**
 * GET 请求
 * @param url 后台地址
 * @param query 请求query参数
 * @param header 请求头，默认为json格式
 * @returns
 */
export const httpGet = <T>(
  url: string,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url: url,
    query,
    method: 'GET',
    header,
  })
}

/**
 * POST 请求
 * @param url 后台地址
 * @param data 请求body参数
 * @param query 请求query参数，post请求也支持query，很多微信接口都需要
 * @param header 请求头，默认为json格式
 * @returns
 */
export const httpPost = <T>(
  url: string,
  data?: Record<string, any>,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    query,
    data,
    method: 'POST',
    header,
  })
}
/**
 * PUT 请求
 */
export const httpPut = <T>(
  url: string,
  data?: Record<string, any>,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    data,
    query,
    method: 'PUT',
    header,
  })
}

/**
 * DELETE 请求（无请求体，仅 query）
 */
export const httpDelete = <T>(
  url: string,
  query?: Record<string, any>,
  header?: Record<string, any>,
) => {
  return http<T>({
    url,
    query,
    method: 'DELETE',
    header,
  })
}

http.get = httpGet
http.post = httpPost
http.put = httpPut
http.delete = httpDelete
